import { useEffect, useRef } from "react";
import { useParams, Navigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { Alert, AlertTitle, Box, Button, Stack } from "@mui/material";
import Replay from "@mui/icons-material/Replay";

import {
  PaymentLoadingContent,
  RegistrationNotFound,
  RegistrationStepPageLayout,
} from "components";
import { api, OnlinePaymentStatus } from "api";
import { extractApiError } from "utils";
import { useConfirmationQueries } from "./usePaymentQueries";

type PageState = "loading" | "initiating" | "initError" | "successful";

export const Payment = () => {
  const { t } = useTranslation();
  const { guid } = useParams<{ guid: string }>();
  const hasInitiatedRef = useRef(false);

  const {
    paymentContext,
    examDetails,
    isContextNotFound,
    isContextError,
    isContextFetching,
  } = useConfirmationQueries(guid);

  const {
    mutate: initiatePayment,
    isPending: isInitiating,
    isError: isInitiateError,
    error: initiateError,
  } = useMutation({
    mutationFn: async () => {
      const result = await api.customers.initiatePayment(guid!);
      return result.data;
    },
    onSuccess: (data) => {
      window.location.href = data.paymentPageUrl;
    },
  });

  const status = paymentContext?.paymentStatus;
  const isPayable =
    status === OnlinePaymentStatus.NotStarted ||
    status === OnlinePaymentStatus.Initialized ||
    status === OnlinePaymentStatus.Failed ||
    status === OnlinePaymentStatus.Abandoned;

  // ── Auto-initiate when context loads and payment is due ──────────────────
  useEffect(() => {
    if (isPayable && !hasInitiatedRef.current) {
      hasInitiatedRef.current = true;
      initiatePayment();
    }
  }, [isPayable, initiatePayment]);

  // ── Derived display state ────────────────────────────────────────────────
  const derivePageState = (): PageState => {
    if (!paymentContext || isContextFetching) return "loading";
    if (isInitiating || (isPayable && !isInitiateError)) return "initiating";
    if (isInitiateError) return "initError";
    return "successful"; // only Successful status reaches here
  };
  const pageState = derivePageState();

  if (isContextNotFound) {
    return <RegistrationNotFound />;
  }

  if (!guid || isContextError) {
    return <Alert severity="error">{t("common.generalError")}</Alert>;
  }

  if (pageState === "successful") {
    return <Navigate to={`/confirmation/${guid}`} replace />;
  }

  return (
    <RegistrationStepPageLayout
      subTitle={t("payment.title")}
      selectedExam={examDetails}
      stepIndex={0}
      completed
      backToHome
      hideStepper
      paymentStatus={status}
      examCardPlaceholder
      priceOverride={paymentContext?.amount}
      currencyOverride={paymentContext?.currency}
    >
      {pageState === "loading" && (
        <PaymentLoadingContent
          icon="card"
          title={t("payment.loading.title")}
          subtitle={t("payment.loading.subtitle")}
          body={t("payment.loading.body")}
        />
      )}
      {pageState === "initiating" && (
        <PaymentLoadingContent
          icon="arrow"
          title={t("payment.initiating.title")}
          subtitle={t("payment.initiating.subtitle")}
          body={t("payment.initiating.body")}
        />
      )}
      {pageState === "initError" && (
        <Stack sx={{ gap: 2, mb: 4 }}>
          <Alert severity="error">
            <AlertTitle>{t("payment.initiateError")}</AlertTitle>
            {extractApiError(initiateError)}
          </Alert>
          <Box>
            <Button
              variant="contained"
              onClick={() => initiatePayment()}
              startIcon={<Replay />}
            >
              {t("payment.retry")}
            </Button>
          </Box>
        </Stack>
      )}
    </RegistrationStepPageLayout>
  );
};
